import { useState } from 'react';
import { useLoaded } from '../lib/useLoaded.ts';
import { generarEntregable } from '../adapters/http.ts';
import { LoadingNotice, EmptyNotice, ErrorNotice } from '../components/StateNotice.tsx';
import { PIPELINE_STAGES, type PipelineStage, type Project } from '../lib/contracts.ts';
import type { DataPort } from '../adapters/port.ts';

/**
 * Creative Pipeline — una fila por proyecto con su barra de progreso, como en la v1.0.
 *
 * 🔑 El porcentaje NO se escribe: se DERIVA de las nueve etapas. `done` y `skipped`
 * cuentan como resueltas. Mostrar un número inventado en una barra sería exactamente
 * la «cifra escrita a mano» que el sistema prohíbe.
 */

const TONO: Record<string, string> = {
  done: 'ok', running: 'blue', blocked: 'red', pending: '', skipped: '',
};

const ETIQUETA: Record<string, string> = {
  done: 'Aprobado', running: 'En producción', blocked: 'Bloqueado',
  pending: 'Brief', skipped: 'Omitido',
};

interface Fila {
  readonly projectId: string;
  readonly code: string;
  readonly nombre: string;
  readonly pct: number;
  readonly estado: string;
  readonly tono: string;
  readonly etapa: string;
}

/**
 * Deriva progreso y estado de las nueve etapas de cada proyecto.
 * El nombre sale del cruce con `projects()`: mostrar el UUID no le dice nada a nadie.
 */
function derivar(stages: readonly PipelineStage[], proyectos: readonly Project[]): Fila[] {
  const nombres = new Map(proyectos.map((p) => [p.id, p.name] as const));
  const codigos = new Map(proyectos.map((p) => [p.id, p.code] as const));
  const porProyecto = new Map<string, PipelineStage[]>();
  for (const s of stages) {
    const l = porProyecto.get(s.projectId) ?? [];
    l.push(s);
    porProyecto.set(s.projectId, l);
  }

  return [...porProyecto.entries()].map(([projectId, list]) => {
    const resueltas = list.filter((s) => s.state === 'done' || s.state === 'skipped').length;
    const pct = Math.round((resueltas / PIPELINE_STAGES.length) * 100);
    const bloqueada = list.find((s) => s.state === 'blocked');
    const corriendo = list.find((s) => s.state === 'running');
    const actual = bloqueada ?? corriendo ?? list.find((s) => s.state === 'pending');
    const estado = bloqueada ? 'blocked' : corriendo ? 'running' : pct === 100 ? 'done' : 'pending';
    return {
      projectId,
      code: codigos.get(projectId) ?? '',
      nombre: nombres.get(projectId) ?? projectId,
      pct,
      estado: ETIQUETA[estado] ?? estado,
      tono: TONO[estado] ?? '',
      etapa: actual ? actual.label : 'Entrega',
    };
  });
}

export function Pipeline({ port }: { port: DataPort }) {
  const s = useLoaded(() => port.stages());
  const pr = useLoaded(() => port.projects());
  const proyectos = pr.kind === 'ok' ? pr.value : [];

  return (
    <section>
      <p className="lede">
        Cada brief aprobado avanza por etapas hasta Human Review. Vista de solo lectura
        del estado de cada pieza.
      </p>

      {s.kind === 'loading' && <LoadingNotice what="las etapas" />}
      {s.kind === 'error' && <ErrorNotice message={s.message} />}
      {s.kind === 'empty' && <EmptyNotice what="etapas" />}
      {s.kind === 'ok' && (
        <div className="rows">
          {derivar(s.value, proyectos).map((f) => (
            <article className="row" key={f.projectId}>
              <div className="row__main">
                <div className="row__name">{f.nombre}</div>
                <div className="row__meta">Etapa actual: {f.etapa}</div>
              </div>

              <div className="progress">
                <div className="progress__head">
                  <span>Progreso</span>
                  <span className="progress__pct">{f.pct}%</span>
                </div>
                <div
                  className="progress__track"
                  role="progressbar" aria-valuenow={f.pct} aria-valuemin={0} aria-valuemax={100}
                  aria-label={`Progreso de ${f.nombre}`}
                >
                  <div className="progress__fill" style={{ width: `${f.pct}%` }} />
                </div>
              </div>

              <span className={'chip-state' + (f.tono ? ` chip-state--${f.tono}` : '')}>{f.estado}</span>
              {f.code && <GenerarEntregable code={f.code} />}
            </article>
          ))}
        </div>
      )}

      <p className="note">
        El porcentaje se deriva de las nueve etapas —«hechas» más «omitidas» sobre el total—.
        Ninguna cifra está escrita a mano.
      </p>
    </section>
  );
}

/**
 * Dispara el motor para un proyecto aprobado. La pieza no aparece acá: el worker
 * la escribe en segundo plano y se ve en Biblioteca. Por eso el botón sólo
 * confirma que arrancó, no espera el resultado.
 */
function GenerarEntregable({ code }: { code: string }) {
  const [estado, setEstado] = useState<'idle' | 'enviando' | 'listo' | string>('idle');

  async function generar() {
    if (estado === 'enviando') return;
    setEstado('enviando');
    const r = await generarEntregable(code);
    setEstado(r === 'ok' ? 'listo' : r);
  }

  if (estado === 'listo') {
    return <span className="row__gen row__gen--ok">El motor está generando — mirá Biblioteca en un minuto</span>;
  }
  return (
    <div className="row__gen">
      <button type="button" className="btn-ghost" disabled={estado === 'enviando'} onClick={() => void generar()}>
        {estado === 'enviando' ? 'Arrancando el motor…' : 'Generar entregable'}
      </button>
      {estado !== 'idle' && estado !== 'enviando' && <span className="row__gen--err">{estado}</span>}
    </div>
  );
}
